import { HTTPError } from 'ky';

import { fastApi } from '@/services/api';
import type { AmlAnswers } from './aml';
import type { Identity } from './identity';
import type { NationalClientIdentifier } from './national-client-identifier';

const PATH = 'v1/customer/periodic-review';

const CONFIRMATION_PATH = 'v1/customer/form/periodic-review';

const IDENTITY_PATH = 'v1/customer/form/periodic-review-personal-identity';

const NATIONAL_CLIENT_IDENTIFIER_PATH = 'v1/customer/form/periodic-review-national-client-identifier';

const AML_PATH = 'v1/customer/form/periodic-review-aml';

// It resets Sumsub and publishes events before it answers, which ky's default 10 seconds could cut off
const CONFIRMATION_TIMEOUT_MS = 30_000;

export interface PeriodicReview {
  status: 'NOT_SCHEDULED' | 'SCHEDULED' | 'DUE';
  reminder_at?: string;
  due_at?: string;
}

export const getPeriodicReview = async () => {
  return fastApi.get(PATH).json<PeriodicReview>();
};

export interface PeriodicReviewIdentity {
  answers: Identity;
  locked: {
    birthInfo?: Partial<Identity['birthInfo']>;
  };
}

export const getPeriodicReviewIdentity = async () => {
  return fastApi
    .get(IDENTITY_PATH)
    .json<PeriodicReviewIdentity>()
    .catch((error) => {
      if (error instanceof HTTPError && error.response.status === 404) return null;

      throw error;
    });
};

export const postPeriodicReviewIdentity = async (data: Identity) => {
  return fastApi.post(IDENTITY_PATH, { json: { answers: data } });
};

export const getPeriodicReviewNationalClientIdentifier = async () => {
  return fastApi
    .get(NATIONAL_CLIENT_IDENTIFIER_PATH)
    .json<NationalClientIdentifier>()
    .catch((error) => {
      if (error instanceof HTTPError && error.response.status === 404) return null;

      throw error;
    });
};

export const postPeriodicReviewNationalClientIdentifier = async (data: NationalClientIdentifier) => {
  return fastApi.post(NATIONAL_CLIENT_IDENTIFIER_PATH, { json: data }).json<NationalClientIdentifier>();
};

export interface PeriodicReviewAml {
  answers: AmlAnswers;
  /** When the customer last saved a draft, absent while these are their live answers */
  date?: string;
}

export const getPeriodicReviewAml = async () => {
  return fastApi
    .get(AML_PATH)
    .json<PeriodicReviewAml>()
    .catch((error) => {
      if (error instanceof HTTPError && error.response.status === 404) return null;

      throw error;
    });
};

export const postPeriodicReviewAml = async (data: PeriodicReviewAml) => {
  return fastApi.post(AML_PATH, { json: data }).json<PeriodicReviewAml>();
};

export interface PeriodicReviewConfirmation {
  confirmedAt: string;
  verificationSteps: string[];
  isFinished: boolean;
}

export const getPeriodicReviewConfirmation = async () => {
  return fastApi
    .get(CONFIRMATION_PATH)
    .json<PeriodicReviewConfirmation>()
    .catch((error) => {
      if (error instanceof HTTPError && error.response.status === 404) return null;

      throw error;
    });
};

export const postPeriodicReviewConfirmation = async () => {
  return fastApi.post(CONFIRMATION_PATH, { json: {}, timeout: CONFIRMATION_TIMEOUT_MS }).json<Omit<PeriodicReviewConfirmation, 'isFinished'>>();
};
